import { CloseIcon, SearchIcon } from 'components/common/Icons';
import { SearchIconGradient } from 'components/common/Icons/gradientIcons';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { FilterContext } from 'utils/context';
import { useDebouncedCallback } from 'utils/hooks';

import {
  Box,
  IconButton,
  Input,
  StyledSystem,
  useTheme,
} from '@project44/manifest-ui-deprecated';

const getCommonStyles = (theme: StyledSystem.Theme) => ({
  clearBtnFocus: {
    boxShadow: 'none',
    outline: 'none',
  },
  inputFocus: {
    borderRadius: '4px',
    boxShadow: `${theme.colors?.fill?.cerulean?.[100]} 0px 0px 0px 2px`,
    border: `1px solid ${theme.colors?.primary?.[500]}`,
  },
});

const getStyles = (theme: StyledSystem.Theme) => {
  const commonStyles = getCommonStyles(theme);

  return {
    root: {
      borderRadius: '4px',
      backgroundColor: 'white',
      width: '280px',
      '& .manifestui-input-endIcon': {
        pointerEvents: 'auto',
        padding: 0,
      },
      '& .manifestui-input-startIcon': {
        paddingLeft: 3,
        paddingRight: 2,
      },
      '& .manifestui-input-container': {
        borderRadius: '4px',
      },
      '& .manifestui-input-container::after': {
        inset: 0,
        borderColor: 'transparent',
      },
      '& .manifestui-input-container:focus::after': commonStyles.inputFocus,
      '& .manifestui-input-container[data-focus]::after':
        commonStyles.inputFocus,
    },
    clearBtn: {
      paddingRight: 3,
      paddingLeft: 2,
      border: 'none',
      '&:focus::after': commonStyles.clearBtnFocus,
      '&[data-focus]::after': commonStyles.clearBtnFocus,
    },
  };
};

interface Props {
  id: string;
  placeholder: string;
  doSearch: () => void;
  sx?: StyledSystem.SystemStyleObject;
}

const FilterInput = ({ id, placeholder, doSearch, sx }: Props) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const { filters, setFiltersKey } = useContext(FilterContext);
  const filter = (filters[id] as string) || '';
  const [searchTerm, setSearchTerm] = useState(filter);

  const searchShipments = useCallback(
    (id: string, value: string) => {
      setFiltersKey(id, value);
      doSearch();
    },
    [doSearch, setFiltersKey],
  );

  const doSearchDebounced = useDebouncedCallback(searchShipments);

  useEffect(() => {
    doSearchDebounced(id, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchTerm]);

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchTerm(value);
  };

  const handleClearInput = () => {
    setSearchTerm('');
    setFiltersKey(id, '');
    doSearch();
  };

  return (
    <Box sx={{ ...styles.root, ...sx }}>
      <Input
        data-testid="search-text"
        onChange={handleChangeInput}
        placeholder={placeholder}
        startIcon={
          !!searchTerm ? (
            <SearchIcon fill="#181819" size="14px" />
          ) : (
            <SearchIconGradient />
          )
        }
        endIcon={
          !!searchTerm && (
            <IconButton
              variant="outlined"
              onClick={handleClearInput}
              sx={styles.clearBtn}
            >
              <CloseIcon fill={theme?.colors?.neutral?.['500']} size="12px" />
            </IconButton>
          )
        }
        value={searchTerm}
      />
    </Box>
  );
};

export default FilterInput;
